import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Ativo, AtivoFormData } from '@/types/ativo';
import { useToast } from '@/hooks/use-toast';
import { useClientFilter } from './useClientFilter';

const transformAtivoFromDB = (ativo: any): Ativo => {
  return {
    ...ativo,
    client: ativo.clients ? {
      id: ativo.clients.id,
      name: ativo.clients.name
    } : undefined,
    fabricante: ativo.fabricantes ? {
      id: ativo.fabricantes.id,
      nome: ativo.fabricantes.nome
    } : undefined,
    contrato: ativo.contratos ? {
      id: ativo.contratos.id,
      numero_contrato: ativo.contratos.numero_contrato
    } : undefined,
    localizacao: ativo.localizacoes ? {
      id: ativo.localizacoes.id,
      nome: ativo.localizacoes.nome
    } : undefined,
    tipo_ativo: ativo.cmdb_tipos_ativos ? {
      id: ativo.cmdb_tipos_ativos.id,
      nome: ativo.cmdb_tipos_ativos.nome
    } : undefined,
    dono_negocio: ativo.dono_negocio ? {
      id: ativo.dono_negocio.id,
      name: ativo.dono_negocio.name
    } : undefined,
    grupo_responsavel: ativo.groups ? {
      id: ativo.groups.id,
      name: ativo.groups.name
    } : undefined,
    proprietario: ativo.proprietario ? {
      id: ativo.proprietario.id,
      name: ativo.proprietario.name
    } : undefined
  };
};

export const useAtivos = () => {
  const { toast } = useToast();
  const { currentClientId, showAllClients, clientLoading } = useClientFilter();

  return useQuery({
    queryKey: ['ativos', currentClientId, showAllClients],
    queryFn: async () => {
      let query = supabase
        .from('cmdb_ativos')
        .select(`
          *,
          clients(id, name),
          fabricantes(id, nome),
          contratos(id, numero_contrato),
          localizacoes(id, nome),
          dono_negocio:users!dono_negocio_id(id, name),
          groups(id, name),
          proprietario:users!proprietario_id(id, name)
        `);

      // Aplicar filtro de cliente se necessário
      if (currentClientId && !showAllClients) {
        query = query.eq('client_id', currentClientId);
      }

      const { data: ativosData, error: ativosError } = await query.order('nome');

      if (ativosError) {
        throw ativosError;
      }

      const { data: tiposData, error: tiposError } = await supabase
        .from('cmdb_tipos_ativos')
        .select('id, nome');

      if (tiposError) {
        throw tiposError;
      }

      const tiposMap = new Map(tiposData?.map(tipo => [tipo.id, tipo]) || []);

      const ativosComTipo = ativosData?.map(ativo => ({
        ...ativo,
        cmdb_tipos_ativos: ativo.tipo_id ? tiposMap.get(ativo.tipo_id) : null
      })) || [];

      return ativosComTipo.map(transformAtivoFromDB);
    },
    enabled: !clientLoading,
  });
};

export const useAtivoById = (id: string | null) => {
  return useQuery({
    queryKey: ['ativo', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cmdb_ativos')
        .select(`
          *,
          clients(id, name),
          fabricantes(id, nome),
          contratos(id, numero_contrato),
          localizacoes(id, nome),
          dono_negocio:users!dono_negocio_id(id, name),
          groups(id, name),
          proprietario:users!proprietario_id(id, name)
        `)
        .eq('id', id!)
        .single();
      if (error) throw error;

      const { data: tiposData } = await supabase
        .from('cmdb_tipos_ativos')
        .select('id, nome');

      const tiposMap = new Map((tiposData ?? []).map(t => [t.id, t]));
      const ativoComTipo = { ...data, cmdb_tipos_ativos: data.tipo_id ? tiposMap.get(data.tipo_id) : null };
      return transformAtivoFromDB(ativoComTipo);
    },
    enabled: !!id,
    staleTime: 30_000,
  });
};

export const useCreateAtivo = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (ativoData: AtivoFormData) => {
      
      const { data, error } = await supabase
        .from('cmdb_ativos')
        .insert([ativoData as any])
        .select()
        .single();

      if (error) {
        console.error('Error creating ativo:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ativos'] });
      toast({
        title: 'Sucesso',
        description: 'Ativo criado com sucesso!',
      });
    },
    onError: (error) => {
      console.error('Error in create ativo mutation:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao criar ativo. Tente novamente.',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateAtivo = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...ativoData }: AtivoFormData & { id: string }) => {
      
      const { data, error } = await supabase
        .from('cmdb_ativos')
        .update(ativoData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating ativo:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ativos'] });
      toast({
        title: 'Sucesso',
        description: 'Ativo atualizado com sucesso!',
      });
    },
    onError: (error) => {
      console.error('Error in update ativo mutation:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar ativo. Tente novamente.',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteAtivo = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      
      const { error } = await supabase
        .from('cmdb_ativos')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting ativo:', error);
        throw error;
      }

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ativos'] });
      toast({
        title: 'Sucesso',
        description: 'Ativo excluído com sucesso!',
      });
    },
    onError: (error) => {
      console.error('Error in delete ativo mutation:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir ativo. Tente novamente.',
        variant: 'destructive',
      });
    },
  });
};
